import conversions from '@sstdev/lib_epc-conversions';
import { styled, Text, View } from 'lib_ui-primitives';
import { rfidStrategy } from 'lib_ui-services';
import PropTypes from 'prop-types';
import { forwardRef, createElement as rc, useCallback, useEffect, useState } from 'react';
import useFormControl from '../../hooks/useFormControl';
import ScanTextInput from '../ScanTextInput';
import FormField from './FormField';

const {
    STRATEGY_TYPES: { GS1, GID, HEX, ASCII, _ID }
} = rfidStrategy;

const { gs1 } = conversions;

const defaultHexRegex = new RegExp('^[0-9A-F]*$', 'i');
const defaultEpcTagUriRegex = new RegExp('^urn:epc:tag:.*$', 'i');

const StyledTextInput = styled(ScanTextInput)`
    flex-grow: 1;
    margin-top: ${({ theme }) => (theme.selectedTheme === 'gl2025' ? 0 : '6px')};
`;

const DetailsBox = styled(View)`
    position: absolute;
    font-size: 12px;

    visibility: hidden;
    background-color: rgba(0, 0, 0, 0.9);
    flex-direction: column;
    display: flex;
    padding-top: 6px;
    padding-left: 20px;
    padding-bottom: 6px;
    padding-right: 12px;
    top: 100%;
    border-radius: 4px;
    z-index: 999;

    & :first-child {
        margin-left: -12px;
        font-weight: bold;
    }
`;

const Detail = styled(Text)`
    font-size: 14px;
    line-height: 1.6;
    color: white;
`;

function getEffectiveStrategy(config, value) {
    return (
        config.displayStrategy ||
        (value.length > config.maxShortLength ? config.longStrategy : config.shortStrategy)
    );
}

const _p = {
    useFormControl,
    getDisplayValue: rfidStrategy.getDisplayValue,
    getRfidConfig: rfidStrategy.getRfidConfig,
    getEffectiveStrategy
};
export const _private = _p;

const ShortEncodedText = forwardRef((props, ref) => {
    const {
        autoComplete = 'off',
        hNode,
        hNode: {
            id,
            max,
            maxLength = 256,
            min,
            minLength = 0,
            placeholder,
            propertyName,
            rows = 1,
            treePosition: { sequence },
            required = false
        },
        type = 'textbox',
        onKeyPress,
        onChange: _onChange
    } = props;

    const {
        title,
        value = '',
        setValue,
        disabled,
        errors: formErrors = [],
        onBlur: _onBlur,
        onFocus,
        active
    } = _p.useFormControl(props);

    const [displayValue, setDisplayValue] = useState('');
    const [errors, setErrors] = useState(formErrors);

    const [inputSource, setInputSource] = useState();
    const [currentConfig, setCurrentConfig] = useState(_p.getRfidConfig());
    const [details, setDetails] = useState([]);

    // getRfidConfig sometimes lags. Use useEffect to ensure we rerender if the config chances due to catching up.
    const _config = _p.getRfidConfig();
    useEffect(() => {
        setCurrentConfig(old => {
            if (
                old &&
                old.shortStrategy === _config.shortStrategy &&
                old.longStrategy === _config.longStrategy &&
                old.maxShortLength === _config.maxShortLength &&
                old.displayStrategy === _config.displayStrategy
            ) {
                return old; // No change, avoid unnecessary state updates
            }
            return _config;
        });
    }, [_config, _config.displayStrategy, _config.longStrategy, _config.maxShortLength, _config.shortStrategy]);

    // Keep displayed value and errors in sync with what is set on the record
    // e.g. when a different record is selected, this displays the new value
    useEffect(() => {
        setErrors(formErrors);
        setDetails([]);
        try {
            // Initialize displayValue with the current value
            const newDisplayValue = _p.getDisplayValue(value);
            setDisplayValue(old => {
                if (old === newDisplayValue) return old; // Avoid unnecessary state updates
                return newDisplayValue;
            });
            if (currentConfig.displayStrategy === ASCII) {
                // confirm that that the hex in displayValue indeed decodes back to the original hex
                const hex = rfidStrategy.getTagId(newDisplayValue);
                if (value !== hex) {
                    setDetails(breakOutDetails(value, HEX));
                    setErrors([
                        'Stored tag ID is not valid for conversion to ASCII. Modification will result in data loss.'
                    ]);
                    return;
                }
            }
            setDetails(breakOutDetails(value, currentConfig.displayStrategy));
        } catch (error) {
            setErrors([error.message || 'Invalid value format']);
            // Fallback to raw value if parsing fails
            setDisplayValue(old => {
                if (old === value) return old; // Avoid unnecessary state updates
                return value;
            });
            setDetails(breakOutDetails(value, HEX));
        }
    }, [formErrors, value, currentConfig.displayStrategy]);

    // When the user types into this component, this assures that we update the display value and errors
    // to match the current input. This WILL be out of sync with the value on the record until the user leaves the field.
    const onChange = useCallback(
        (userInput, inputSource) => {
            setInputSource(inputSource);
            setDisplayValue(userInput);
            setErrors([]);
            setDetails([]);
            // re-enable the following when we have a means to configure the maxShortLength:
            // if (userInput.length > currentConfig.maxShortLength && currentConfig.longStrategy === _ID) {
            //     setErrors([`Value too long. Unable to store as ${currentConfig.shortStrategy}`]);
            //     return;
            // }
            const currentStrategy = getEffectiveStrategy(currentConfig, userInput);

            switch (currentStrategy) {
                case GS1:
                case GID: {
                    if (userInput.length < 4) {
                        setErrors(['Please enter a valid EPC Tag URI, or its hex value.']);
                    } else {
                        try {
                            // if they entered valid hex
                            if (userInput.match(defaultHexRegex)) {
                                // see if it converts to a valid EPC Tag URI
                                setDetails(breakOutDetails(userInput, currentStrategy));
                                break;
                            }
                            // if it looks like a valid EPC Tag URI, try to get the hex value from it
                            else if (userInput.match(defaultEpcTagUriRegex)) {
                                const result = rfidStrategy.getTagId(userInput);
                                // if it didn't return the input unchanged, It's valid!
                                if (result !== userInput) {
                                    setDetails(breakOutDetails(result, currentStrategy));
                                }
                                break;
                            }
                            //otherwise, display an error!
                            setErrors(['Please enter a valid EPC Tag URI, or its hex value.']);
                            setDetails([]);
                        } catch (e) {
                            setErrors([e.message]);
                            setDetails([]);
                        }
                    }
                    break;
                }

                case ASCII: {
                    // getTagId selects its encoding strategy by length against the configured
                    // short/longStrategy. For a typical ASCII tenant (maxShortLength=12,
                    // longStrategy='_id'), a value longer than 12 chars would go through the _id
                    // path and produce `1A${value}` — not hex. Reject it here with a clear message.
                    if (userInput.length > currentConfig.maxShortLength) {
                        setErrors([`Value too long for ASCII mode. Maximum ${currentConfig.maxShortLength} characters.`]);
                        setDetails([]);
                        break;
                    }
                    // however, if the original value was hex that does not convert to clean ascii, use that
                    const newHex = rfidStrategy.getTagId(userInput);
                    if (rfidStrategy.getDisplayValue(value) === userInput && newHex !== value) {
                        setDetails(breakOutDetails(value, HEX));
                        setErrors([
                            'Stored tag ID is not valid for conversion to ASCII. Modification will result in data loss.'
                        ]);
                    } else {
                        setDetails(breakOutDetails(newHex, currentStrategy));
                        // set the value on the form
                        setValue(newHex, inputSource);
                        // trigger passed in onChange callback if it exists
                        _onChange?.(newHex, inputSource);
                    }
                    break;
                }
                case _ID:
                case HEX:
                default:
                    if (!userInput.match(defaultHexRegex)) {
                        setErrors(['Only 0-9 and A-F characters are allowed.']);
                        setDetails([]);
                    } else {
                        setDetails(breakOutDetails(rfidStrategy.getTagId(userInput), currentStrategy));
                        // set the value on the form
                        setValue(userInput, inputSource);
                        // trigger passed in onChange callback if it exists
                        _onChange?.(userInput, inputSource);
                    }
                    break;
            }
        },
        [
            _onChange,
            currentConfig.displayStrategy,
            currentConfig.longStrategy,
            currentConfig.maxShortLength,
            currentConfig.shortStrategy,
            setValue,
            value
        ]
    );

    // this is used to persist the input.
    const onBlur = useCallback(
        e => {
            const userInput = displayValue.trim();
            const currentStrategy = getEffectiveStrategy(currentConfig, userInput);

            // re-process to display error messages if necessary
            onChange(userInput, inputSource);

            let result = userInput;
            switch (currentStrategy) {
                case GS1:
                case GID:
                    // E.g. Epc Tag URN  urn:epc:tag:sgtin-96:1234567.123456.1234567890123
                    // or hex:  3031323334353637383930

                    try {
                        // if they entered valid hex
                        if (userInput.match(defaultHexRegex)) {
                            // see if it converts to a valid EPC Tag URI
                            result = _p.getDisplayValue(userInput);
                        }
                        // if it looks like a valid EPC Tag URI, try to get the hex value from it
                        else if (userInput.match(defaultEpcTagUriRegex)) {
                            result = rfidStrategy.getTagId(userInput);
                        }
                    } catch {
                        // errors already shown in validation, if applicable.
                        // ignore the error here
                    }
                    break;

                case ASCII: {
                    // Mirror the onChange guard: a long input would go through longStrategy
                    // (typically '_id') in getTagId and produce a corrupted non-hex value.
                    // Keep the existing stored value unchanged; onChange already set the error.
                    if (userInput.length > currentConfig.maxShortLength) {
                        result = value; // keep existing stored value; onChange already set the error
                        break;
                    }
                    if (rfidStrategy.getDisplayValue(value) === userInput) {
                        result = value;
                    } else {
                        result = rfidStrategy.getTagId(userInput);
                    }
                    result = rfidStrategy.normalizeTagIdHex(result);
                    break;
                }
                case HEX:
                default: {
                    result = userInput.toUpperCase();
                    result = rfidStrategy.normalizeTagIdHex(result);
                    break;
                }
            }
            if (result !== value) {
                // set the value on the form
                setValue(result, inputSource);
                // trigger passed in onChange callback if it exists
                _onChange?.(result, inputSource);
            }
            // update focus state
            _onBlur?.(e);
        },
        [
            displayValue,
            currentConfig.displayStrategy,
            currentConfig.maxShortLength,
            currentConfig.longStrategy,
            currentConfig.shortStrategy,
            onChange,
            inputSource,
            setValue,
            value,
            _onChange,
            _onBlur
        ]
    );

    const currentStrategy = getEffectiveStrategy(currentConfig, value);
    const pattern =
        currentStrategy === GS1 || currentStrategy === GID
            ? defaultEpcTagUriRegex
            : currentStrategy === ASCII
              ? undefined
              : defaultHexRegex;

    const fieldEmpty = value === '';
    const displayDetails = details.length > 0;
    // prettier-ignore
    return rc(FormField, { hNode, title, errors, active, fieldEmpty, onBlur, required},
        displayDetails && rc(DetailsBox, {
                id: `details-${id}`,
                'data-testid': `details-${id}`,
                'aria-label':'Tag Details',
                style: { visibility: active ? 'visible' : 'hidden' }
            },
            details.map((detail,i)=> rc(Detail, { key: i }, detail))
        ),
        rc(StyledTextInput, {
            hNode,
            active,
            ref,
            id,
            name: propertyName,
            type,
            disabled,
            autoComplete,
            spellCheck: false,
            max,
            maxLength,
            min,
            minLength,
            pattern,
            placeholder,
            multiline: true,
            rows: (rows === 1 && displayValue.length > 24) ? 3 : rows,
            value: displayValue,
            onKeyPress,
            onChange,
            onFocus,
            sequence
        })
    );
});

function breakOutDetails(hex, strategy) {
    if (!hex) {
        return [];
    }
    let details = ['DETAILS:'];
    switch (strategy) {
        case GS1:
        case GID: {
            if (hex.length < 4) {
                return [];
            }
            const epc = gs1.parse(hex);
            if (epc.type) {
                // the most default: SGTIN information:
                details.push('Type: ' + epc.type);
                if (typeof epc.filter !== 'undefined') {
                    details.push('Product Category Filter: ' + epc.filter);
                }
                if (epc.company) {
                    details.push('GS1 Company Prefix: ' + epc.company);
                }
                if (typeof epc.productNr !== 'undefined') {
                    // SGTIN / GID
                    details.push('Product Number: ' + epc.productNr);
                    details.push('Serial: ' + epc.serial);
                    if (typeof epc.gtin !== 'undefined' && epc.gtin !== null) {
                        details.push('GTIN: ' + epc.gtin);
                    }
                } else if (typeof epc.serial !== 'undefined') {
                    details.push('Serial: ' + epc.serial);
                }
                if (typeof epc.assetReference !== 'undefined') {
                    // GIAI
                    details.push('Asset Reference: ' + epc.assetReference);
                }
                if (typeof epc.assetType !== 'undefined') {
                    // GRAI
                    details.push('Asset Type: ' + epc.assetType);
                }
                if (typeof epc.documentType !== 'undefined') {
                    // GDTI
                    details.push('Document Type: ' + epc.documentType);
                }
                if (typeof epc.componentPartReference !== 'undefined') {
                    // CPI
                    details.push('Component / Part Reference: ' + epc.componentPartReference);
                }
                if (typeof epc.couponReference !== 'undefined') {
                    // SGCN
                    details.push('Coupon Reference: ' + epc.couponReference);
                }
                if (typeof epc.serviceReference !== 'undefined') {
                    // GSRN
                    details.push('Service Reference: ' + epc.serviceReference);
                }
                if (epc.hex) {
                    details.push('EPC Hex Value: ' + epc.hex);
                }
            }
            break;
        }
        case ASCII: {
            details.push('Type: ASCII');
            details.push('EPC Hex Value: ' + hex);
            break;
        }
        case HEX:
        default: {
            details.push('Type: HEX');
            details.push('EPC Hex Value: ' + hex);
            break;
        }
    }
    return details;
}

ShortEncodedText.displayName = 'ShortEncodedText';
ShortEncodedText.propTypes = {
    hNode: PropTypes.shape({
        treePosition: PropTypes.shape({
            sequence: PropTypes.number.isRequired
        }).isRequired
    }).isRequired,
    autoComplete: PropTypes.string,
    multiline: PropTypes.bool,
    type: PropTypes.string,
    onKeyPress: PropTypes.func,
    onChange: PropTypes.func
};

export default ShortEncodedText;
